import { useState } from "react";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Wrench } from "lucide-react";

const DEFAULT_WINDOW_MS = 4 * 3600e3;

function toLocalInput(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function initialWindow() {
  const now = new Date();
  return { reason: "", start: toLocalInput(now), end: toLocalInput(new Date(now.getTime() + DEFAULT_WINDOW_MS)) };
}

// Mounted only while a machine is selected, so the form re-initialises per machine.
export function DowntimeFormDialog({ machine, onClose, onSubmit }) {
  const [form, setForm] = useState(initialWindow);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      await onSubmit({ machine_id: machine.id, reason: form.reason, start_time: new Date(form.start).toISOString(), end_time: new Date(form.end).toISOString() });
      onClose();
    } catch (err) {
      logger.error("Downtime submit failed (toast shown by handler; modal kept open):", err);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="downtime-form-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Wrench className="h-4 w-4 text-amber-600" />
            Log Downtime — {machine.line_code} · {machine.name}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Reason</Label>
            <Input value={form.reason} onChange={(e) => set("reason", e.target.value)} placeholder="e.g. Preventive maintenance, tooling failure…"
              required minLength={2} data-testid="downtime-form-reason" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Start</Label>
              <Input type="datetime-local" value={form.start} onChange={(e) => set("start", e.target.value)} required data-testid="downtime-form-start" />
            </div>
            <div className="space-y-1.5">
              <Label>End</Label>
              <Input type="datetime-local" value={form.end} onChange={(e) => set("end", e.target.value)} required data-testid="downtime-form-end" />
            </div>
          </div>
          <p className="text-[11px] text-slate-400">Orders scheduled on this line during the window will be flagged with a capacity conflict.</p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-amber-600 hover:bg-amber-700" data-testid="downtime-form-submit">Log Downtime</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
